import React from "react";
import Icon from "assets/icons";
import classNames from "classnames";
import { ImagePreviewConfig, ImageUploadConfig } from "../types";
import { FileRejection, useDropzone, ErrorCode } from "react-dropzone";
import { useCopies } from "shared/hooks/useCopies";
import { ACCEPTED_IMAGE_MIME_TYPE, PDF_MIME_TYPE } from "shared/utils/constants";
import Button from "shared/components/button";
import Variant from "shared/enums/Variant";

/**
 * @todo implement getResolvedString-with-components for copies
 * @improvement ACCEPTED_IMAGE_MIME_TYPE should be configurable
 */

function ImageUpload({
  acceptedFileTypes = ACCEPTED_IMAGE_MIME_TYPE,
  isRemovable = true,
  ...props
}: ImageUploadConfig) {
  const copies = useCopies();
  const [image, setImage] = React.useState<File | string>("");

  const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    noClick: true,
    maxSize: props.maxSize,
    noKeyboard: true,
    accept: acceptedFileTypes,
    disabled: props.disabled,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        const [file] = acceptedFiles; // because we will have 1 file selected at max
        props.handleChange?.(file);
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        });
      }
      if (fileRejections.length > 0) {
        if (fileRejections[0]?.errors[0]?.code === ErrorCode.FileInvalidType) {
          props.handleError?.(copies.get("invalid.file.type"));
        }
      }
    }
  });

  const removeHandler = React.useCallback(() => {
    props.handleChange?.("");
    setImage("");
  }, [props]);

  React.useMemo(() => {
    props.value && setImage(props.value);
  }, [props.value]);

  React.useEffect(() => {
    // Revoking the data uris to avoid memory leaks
    // URL.revokeObjectURL(image.preview);
  }, [image]);

  return (
    <div tabIndex={0} className={classNames("imageUpload", { "imageUpload--disabled": props.disabled })}>
      <div
        {...getRootProps()}
        className={classNames("imageUpload_wrapper", {
          isDragActive: isDragActive
        })}>
        <input {...getInputProps()} disabled={props.disabled} aria-label={props.name} />
        {image ? (
          <React.Fragment>
            {(typeof image === "object" && image.type === PDF_MIME_TYPE) ||
            (typeof image === "string" && props.uploadedFileMimeType === PDF_MIME_TYPE) ? (
              <div className="imageUpload_preview">
                <span>
                  <Icon.PdfFile />
                  {!props.disabled && isRemovable && (
                    <Button
                      title={copies.get("remove.image")}
                      className="closeIcon"
                      variant={Variant.ERROR}
                      onClick={removeHandler}
                      icon={<Icon.CloseOutline />}
                    />
                  )}
                </span>
              </div>
            ) : (
              <ImagePreview
                image={image}
                name={props.name}
                exactPreview={props.exactPreview}
                dimension={props.dimension}
                backgroundColor={props.backgroundColor}
                isRemovable={!props.disabled && isRemovable}
                imageRemoveHandler={removeHandler}
                previewPlaceholderIcon={props.previewPlaceholderIcon}
              />
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            {!props.disabled ? (
              <div className="imageUpload_icon">
                <Icon.UploadImage />
              </div>
            ) : (
              <div className="imageUpload_icon">
                <Icon.NoImageFound />
              </div>
            )}
          </React.Fragment>
        )}
        <div className="imageUpload_content text-center">
          {copies.get(image ? "update.asset.info.msg" : "upload.asset.info.msg")}
          <Button.Link label={copies.get("browse")} onClick={open} disabled={props.disabled} />
        </div>
      </div>
    </div>
  );
}

export default ImageUpload;

export function ImagePreview(props: ImagePreviewConfig) {
  const copies = useCopies();
  return (
    <div
      className={classNames("imageUpload_preview", { "imageUpload_preview--hasBg": props.backgroundColor })}
      key={props.name}>
      <span
        style={{
          height: props.dimension?.height,
          width: props.dimension?.width,
          backgroundColor: props.backgroundColor
        }}>
        {props.previewPlaceholderIcon ? (
          <div className="imageUpload_icon">{props.previewPlaceholderIcon}</div>
        ) : typeof props.image === "string" ? (
          <img src={props.image} alt={props.name} />
        ) : (
          <img src={props.image.preview} alt={props.name} />
        )}
        {props.isRemovable && (
          <Button
            title={copies.get("remove.image")}
            className="closeIcon"
            onClick={props.imageRemoveHandler}
            variant={Variant.ERROR}
            icon={<Icon.CloseOutline />}
          />
        )}
      </span>
      {props.exactPreview && <small>{copies.get("logo.exact.preview")}</small>}
    </div>
  );
}
